import type { UserRole } from "../api/admin";

export interface NavItem {
  label: string;
  path: string;
  icon: string;
  minRole?: UserRole;
  /** Optional keywords to improve command-palette search. */
  keywords?: string;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
  /** Render as a collapsible section (used for rarely-touched groups). */
  collapsible?: boolean;
  /** localStorage key persisting open/closed state when collapsible. */
  storageKey?: string;
}

/**
 * Single source of truth for the admin navigation. Consumed by the Sidebar,
 * the TopBar title, and the command palette so they never drift apart.
 * Ordered by how often each area is used (configuration + devices on top,
 * diagnostics/access below).
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    title: "Configuration",
    items: [
      { label: "Feature Flags", path: "/feature-flags", icon: "ri-toggle-line", keywords: "flags toggle audience" },
      { label: "Payment Methods", path: "/payment-methods", icon: "ri-bank-card-line", keywords: "payment pay method" },
      { label: "Buyer FAQ", path: "/buyer-service-faq", icon: "ri-question-answer-line", keywords: "faq help support questions" },
      { label: "App Updates", path: "/app-updates", icon: "ri-smartphone-line", keywords: "version release rollout update" },
    ],
  },
  {
    title: "Devices & Push",
    items: [
      { label: "Devices", path: "/devices", icon: "ri-device-line", keywords: "device installation token customer" },
      { label: "Push", path: "/push", icon: "ri-notification-3-line", keywords: "push notification delivery test" },
    ],
  },
  {
    title: "Diagnostics",
    collapsible: true,
    storageKey: "nav.diagnostics.open",
    items: [
      { label: "Dashboard", path: "/dashboard", icon: "ri-dashboard-line", keywords: "overview kpi metrics home" },
      { label: "Timeline", path: "/timeline", icon: "ri-timeline-view", keywords: "events operational device customer audit" },
      { label: "Support Traces", path: "/support-traces", icon: "ri-route-line", keywords: "trace support bot session" },
      { label: "Airbridge Links", path: "/airbridge-links", icon: "ri-links-line", keywords: "airbridge tracking links attribution" },
      { label: "System", path: "/system", icon: "ri-server-line", keywords: "health status service" },
    ],
  },
  {
    title: "Access",
    items: [
      { label: "Users", path: "/users", icon: "ri-team-line", keywords: "admin user role access" },
      { label: "Audit Log", path: "/audit-log", icon: "ri-history-line", keywords: "audit security sessions mfa admin" },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

/** Default landing target when there is no remembered route. */
export const DEFAULT_ROUTE = "/feature-flags";

/** Resolve a human page title for the TopBar from the current pathname. */
export function titleForPath(pathname: string): string {
  const match = NAV_ITEMS.find(
    (item) => pathname === item.path || pathname.startsWith(`${item.path}/`),
  );
  return match?.label ?? "Admin";
}
